/**
 * J3「本月剩余可花」契约 —— `GET /api/spendable`（**J 域**）
 *
 * 依据：规格 **§12**（`预算 − 已花 − 必付未付`）· `DR-010` **决策三**（两个「可花」分名分口径）·
 * `DR-012` · J 域 PRD **§4.6** + **§7.3**（`AC-J3.1`–`AC-J3.10`）· 架构 **§6.6**（下发接口面冻结）
 * · **§6.1**（契约唯一源）。
 *
 * ## 🔴 本文件**不定义任何算法**
 *
 * 「可花」的算式在共享层纯函数 `nucleus/budget.ts` 的 `composeSpendable()` 里
 * （唯一实现）；「已花」走 **G 域唯一预算花费函数**（`budgetSpentFrom`）；「必付未付」走
 * **F2 同一份聚合**（`nucleus/upcoming.ts` 的 `composeUpcoming`）—— 本文件只描述**形状**。
 *
 * ## 三个「谁定什么」的边界（铁律 24 防双实现）
 *
 * | 项 | 谁定 | 说明 |
 * |---|---|---|
 * | **名字** | 🔴 **J 域**（本文件的 {@link SPENDABLE_LABEL}） | G 域 PRD §5 原文「G 供数 / **J 定名**」；F 域 PRD `AC-F3.9` 要求与 F3「本次分配后可花」**不同名、不共用函数** |
 * | **预算项（已花 / 预算额）** | 🔴 **G 域** | `AC-J3.3`：必须调 G 域唯一函数，**不自建聚合** |
 * | **必付未付** | 🔴 **F 域** | `AC-J3.5`：数据源 = F2 同一份聚合，**J 域不自算**（J3 是该聚合的**第三个消费者**） |
 *
 * ## ⚠️ 两条「不截断 / 不冒充」的诚实性纪律（与 J1 `notReady` 同源）
 *
 * - **无预算** ⇒ `kind = noBudget` + `value = null` —— 🔴 UI 显示「还没设预算」与引导，
 *   **不显示任何数字**（`AC-J3.8`：拿空预算算出来的数一定是假的）；
 * - **可花为负** ⇒ `kind = overspent` + `overspentBy > 0` —— 🔴 显示「已透支 ¥X」，
 *   **不截断为 0**（`AC-J3.9`：截断会掩盖问题）。
 */

import type { Cents } from '../types/brand.js';
import type { BudgetPeriod } from '../enums/plan.js';
import type { UpcomingKind } from '../nucleus/upcoming.js';
import type { BudgetWindowView } from './budget.js';

/**
 * 🔴 **唯一名字**（`AC-J3.1`）—— 全系统只此一处，Web 与小程序都读它。
 *
 * ⚠️ **禁出现第二个名字**（规格 §12 明文点名删除 v1 的「本周可安全花」）：
 * 名字一旦分叉，用户就会以为是两套口径，而两套口径迟早会变成两个数。
 * ⚠️ F3 的「**本次分配后可花**」是**另一个**名字、**另一套**算法（`DR-010` 决策三）——
 * 那个属于发薪日工作流（F 域），**不得**复用到本处。
 */
export const SPENDABLE_LABEL = '本月剩余可花';

/** 卡片副标题（口径行）—— 与规格 §12 的公式**逐字相同**（端侧不自己编） */
export const SPENDABLE_FORMULA_TEXT = '预算 − 已花 − 必付未付';

/** 「可花」的**三态**（🔴 三态穷举 —— 端侧按 `kind` 分支，不自行判断"是不是负数"） */
export const SpendableKind = {
  /** 该窗口**没有总预算** ⇒ 显示引导，**不显示数字**（`AC-J3.8`） */
  NoBudget: 'noBudget',
  /** 还有得花（`value ≥ 0`） */
  Available: 'available',
  /** 已透支（**不截断为 0** · `AC-J3.9`） */
  Overspent: 'overspent',
} as const;
export type SpendableKind = (typeof SpendableKind)[keyof typeof SpendableKind];

/**
 * 「可花」的**结果三态**。
 *
 * 🔴 `value` 与 `overspentBy` **互斥**，且各在另两态为 `null` ——
 * 用 `null` 而不是 `0` 是为了让「不适用」与「真的是 0」**在类型上就分得开**
 * （同 `NetWorthResponse.notReady` 的纪律：不让 `0` 冒充"这项确实是 0"）。
 */
export interface SpendableView {
  kind: SpendableKind;
  /** 可花金额（本币整数分）；仅 `kind = available` 时非 `null`（**可为 0**） */
  value: Cents | null;
  /** 已透支金额（本币整数分，**正数**）；仅 `kind = overspent` 时非 `null` */
  overspentBy: Cents | null;
}

/**
 * 「可花」的**构成明细**（`AC-J3.6`：点「可花」展开，三项分列）。
 *
 * 🔴 三个数字**全部来自服务端**，端侧**一个都不算**：
 * - `spent` ⇒ G 域唯一预算花费函数（含待缴·未付，口径不开例外）；
 * - `unpaidPending` ⇒ 同一内核的**子集**（`status = pending`）—— 只用于**标注**，
 *   不是第二个口径（`DR-011` §4）；
 * - `upcomingTotal` / `upcomingByKind` ⇒ F2 同一份聚合的三构成分列。
 */
export interface SpendableBreakdownView {
  /** 总预算额；`null` = 该窗口没有总预算 */
  budgetAmount: Cents | null;
  /** 已花（**含**待缴·未付） */
  spent: Cents;
  /** 其中「待缴·未付」部分（仅用于标注；`> 0` 才显示） */
  unpaidPending: Cents;
  /** 必付未付合计（= F2 同一聚合的 `total` —— 缺汇率的项**不计入**） */
  upcomingTotal: Cents;
  /** 三构成各自合计（`AC-J3.4`：信用卡本期应还 / 未付待缴 / 本窗口剩余期次） */
  upcomingByKind: Readonly<Record<UpcomingKind, Cents>>;
}

/**
 * `GET /api/spendable` 的响应体。
 *
 * ## ⚠️ `window` 与本窗口的「剩余天数」口径
 *
 * `window` 是**预算窗口**（半开区间 `[start, end)`，与 G 域同一形状 —— 直接复用
 * {@link BudgetWindowView}，不另立一个）。无预算时退回**当前自然月**
 * （「本月剩余可花」的名字本身就把口径指到月）。
 *
 * 🔴 「必付未付」只取**本窗口剩余天数内到期**的项（`windowStart = 账本今天`）——
 * 依据有两条：① 规格 §12 原文「本窗口**剩余天数内**到期的周期账期次」；
 * ② 原型 `01-今天_原型.html` 的数值反推（预算 14,000 / 已花 3,650 / 必付未付 8,170
 * = 「未来 7 天必付」清单之和 / 已占用 84.4%）。**若把已逾期的待缴也算进来，就会与
 * 「已花（含待缴）」双计** —— 同一笔钱既算花掉又算还没付。
 */
export interface SpendableResponse {
  baseCurrency: string;
  /** 命中的总预算 id；`null` = 无总预算 */
  budgetId: string | null;
  /** 命中总预算的周期；`null` = 无总预算（此时 `window` = 当前自然月） */
  budgetPeriod: BudgetPeriod | null;
  /** 预算窗口（半开区间 `[start, end)`） */
  window: BudgetWindowView;
  /** 可花结果三态 */
  result: SpendableView;
  /** 构成明细（展开用） */
  breakdown: SpendableBreakdownView;
  /**
   * 「已占用」%（`E4`，`10000` = 100%）= `(已花 + 必付未付) / 预算`。
   *
   * 🔴 服务端算好下发 —— 端侧的进度条**不自己除**（浮点除法全项目封死，
   * 且"占用"的定义只该有一处）。无预算 ⇒ `null`（进度条不渲染）。
   * ⚠️ 与 J3 PRD §4.6「可展开」同源：原型该卡是这个形态
   * （`月预算 ¥14,000` / `已占用 84.4%`）。
   */
  occupiedE4: number | null;
  /** 必付未付存在**缺汇率**项 ⇒ 合计**不完整**（端侧必须标注，`DR-004` 禁 1:1 冒充） */
  fxIncomplete: boolean;
}
